import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Webhook } from '@/services/webhookService';
import { CheckCircle2, XCircle, Clock, Shield, Settings, Activity } from 'lucide-react';

interface WebhookOverviewTabProps {
  webhook: Webhook;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return 'Invalid date';
  }
};

const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'Never';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Invalid date';
  }
};

const WebhookOverviewTab: React.FC<WebhookOverviewTabProps> = ({ webhook }) => {
  const isHealthy = webhook.failure_count < 10;
  const successRate = webhook.total_deliveries > 0
    ? ((webhook.total_deliveries - webhook.failure_count) / webhook.total_deliveries * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Basic Information
          </CardTitle>
          <CardDescription>Webhook configuration details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <p className="text-sm text-slate-900 mt-1">
                {webhook.name || 'Unnamed Webhook'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Status</label>
              <div className="mt-1">
                <Badge
                  variant={webhook.enabled ? 'default' : 'secondary'}
                  className={
                    webhook.enabled
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-slate-100 text-slate-800 border-slate-200'
                  }
                >
                  {webhook.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700">Webhook URL</label>
              <p className="text-sm text-slate-900 mt-1 break-all font-mono">
                {webhook.webhook_url}
              </p>
            </div>
            {webhook.description && (
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700">Description</label>
                <p className="text-sm text-slate-900 mt-1">
                  {webhook.description}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Authentication
          </CardTitle>
          <CardDescription>Authentication configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Auth Type</label>
              <p className="text-sm text-slate-900 mt-1 capitalize">
                {webhook.auth_type === 'none' ? 'None' : webhook.auth_type.replace('_', ' ')}
              </p>
            </div>
            {webhook.auth_type !== 'none' && (
              <div>
                <label className="text-sm font-medium text-slate-700">Credentials</label>
                <p className="text-sm text-slate-600 mt-1 italic">
                  Configured (hidden for security)
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration
          </CardTitle>
          <CardDescription>Webhook settings and retry configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Timeout</label>
              <p className="text-sm text-slate-900 mt-1">
                {webhook.timeout_ms}ms
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Retry Enabled</label>
              <div className="mt-1">
                <Badge
                  variant={webhook.retry_config.enabled ? 'default' : 'secondary'}
                >
                  {webhook.retry_config.enabled ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
            {webhook.retry_config.enabled && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700">Max Attempts</label>
                  <p className="text-sm text-slate-900 mt-1">
                    {webhook.retry_config.max_attempts}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Backoff Multiplier</label>
                  <p className="text-sm text-slate-900 mt-1">
                    {webhook.retry_config.backoff_multiplier}x
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscribed Events */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Subscribed Events
          </CardTitle>
          <CardDescription>Events this webhook is subscribed to</CardDescription>
        </CardHeader>
        <CardContent>
          {webhook.subscribed_events.length === 0 ? (
            <p className="text-sm text-slate-600">All events</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {webhook.subscribed_events.map((event) => (
                <Badge key={event} variant="outline">
                  {event}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Statistics
          </CardTitle>
          <CardDescription>Webhook delivery statistics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Total Deliveries</label>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {webhook.total_deliveries}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Failures</label>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {webhook.failure_count}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Success Rate</label>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {successRate}%
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Health Status</label>
              <div className="mt-1">
                <Badge
                  variant={isHealthy ? 'default' : 'destructive'}
                  className={
                    isHealthy
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-red-100 text-red-800 border-red-200'
                  }
                >
                  {isHealthy ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Healthy
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      Unhealthy
                    </>
                  )}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timestamps
          </CardTitle>
          <CardDescription>Important timestamps</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Last Triggered</label>
              <p className="text-sm text-slate-900 mt-1">
                {formatRelativeTime(webhook.last_triggered_at)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {formatDate(webhook.last_triggered_at)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Last Failure</label>
              <p className="text-sm text-slate-900 mt-1">
                {formatRelativeTime(webhook.last_failure_at)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {formatDate(webhook.last_failure_at)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Created</label>
              <p className="text-sm text-slate-900 mt-1">
                {formatDate(webhook.created_at)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Last Updated</label>
              <p className="text-sm text-slate-900 mt-1">
                {formatDate(webhook.updated_at)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookOverviewTab;

